'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  CircularProgress,
  Container,
  Grid,
  Pagination,
  Typography,
} from '@mui/material';
import { GALLERY_PROJECTS_PER_PAGE } from '@/consts/gallery-constants';
import { PORTFOLIO_NAV_COLLECTION_COVERS } from '@/consts/portfolio-nav-images';
import { fetchGalleryCollectionProjects } from '@/utils/gallery-api';
import CollectionSubNavLinks from './CollectionSubNavLinks';
import GalleryProseHtml from './GalleryProseHtml';

export default function CollectionDetailPage({ collection, initialProjectsPage, subNavLinks }) {
  const [page, setPage] = useState(1);
  const [projectsPage, setProjectsPage] = useState(initialProjectsPage);
  const [loading, setLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil((projectsPage?.count ?? 0) / GALLERY_PROJECTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageProjects = projectsPage?.results ?? [];
  // Prefer CMS header/banner; fall back to static nav cover when unset.
  const coverSrc = collection.bannerImage || PORTFOLIO_NAV_COLLECTION_COVERS[collection.slug]?.src;

  useEffect(() => {
    if (page === 1) {
      setProjectsPage(initialProjectsPage);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchGalleryCollectionProjects(collection.slug, {
      limit: GALLERY_PROJECTS_PER_PAGE,
      offset: (page - 1) * GALLERY_PROJECTS_PER_PAGE,
      revalidate: false,
    })
      .then(data => {
        if (!cancelled && data) {
          setProjectsPage(data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, collection.slug, initialProjectsPage]);

  return (
    <Box>
      {coverSrc ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            aspectRatio: { xs: '16 / 9', md: '2.4 / 1' },
            minHeight: { xs: 220, md: 240 },
            maxHeight: { xs: 360, md: 420 },
          }}
        >
          <Box
            component="img"
            src={coverSrc}
            alt=""
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.28) 42%, rgba(0,0,0,0.08) 70%, transparent 100%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <Container maxWidth="xl" sx={{ pb: { xs: 2.5, md: 3.5 } }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#EFE8DC' }}>
                {collection.name}
              </Typography>
            </Container>
          </Box>
        </Box>
      ) : null}

      <Container maxWidth="xl" sx={{ py: 8.5, ...(coverSrc ? { pt: { xs: 4, md: 5 } } : {}) }}>
        {coverSrc ? null : (
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {collection.name}
          </Typography>
        )}
        <GalleryProseHtml
          html={collection.description}
          sx={{ mt: coverSrc ? 0 : 2, width: '100%', maxWidth: 'none' }}
        />

        <CollectionSubNavLinks collectionName={collection.name} links={subNavLinks} />

        {pageProjects.length > 0 ? (
          <>
            <Grid container spacing={3} sx={{ mt: 4, position: 'relative' }}>
              {loading ? (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.6)',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={32} />
                </Box>
              ) : null}
              {pageProjects.map(project => {
                const image = project.thumbnail ? (
                  <CardMedia
                    component="img"
                    src={project.thumbnail}
                    alt=""
                    sx={{ aspectRatio: '16 / 9', objectFit: 'cover', display: 'block' }}
                  />
                ) : null;

                return (
                  <Grid key={project.portfolioSlug} item xs={12} md={6}>
                    <Card sx={{ overflow: 'hidden' }}>
                      {project.href ? (
                        <CardActionArea
                          component={Link}
                          href={project.href}
                          aria-label="View project"
                        >
                          {image}
                        </CardActionArea>
                      ) : (
                        image
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {totalPages > 1 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, value) => setPage(value)}
                  disabled={loading}
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      },
                    },
                  }}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </Container>
    </Box>
  );
}

CollectionDetailPage.propTypes = {
  collection: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    bannerImage: PropTypes.string,
  }).isRequired,
  initialProjectsPage: PropTypes.shape({
    count: PropTypes.number.isRequired,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        thumbnail: PropTypes.string,
        portfolioSlug: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  subNavLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
};
